import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import {orgId, defaultErrorHandler} from '../../../context/context';
import queryParameterUtils from '../../../utils/query-parameter-utils';
import redirectUtils from '../../../utils/redirect-utils';
import CenteredSpinner from '../../../libs/react-components-admin/spinners/centered-spinner';
import Section from '../../../libs/react-components-admin/section/section';
import SubSection from '../../../libs/react-components-admin/section/sub-section';
import WarningBox from '../../../libs/react-components-admin/box/warning-box';
import googleSheetsV1Client from '../../../infrastructure/acerill/google-sheets-v1-client';
import NamedIntegrations from '../../../libs/react-components-admin/integration/named-integrations';
import customFormsV2Client from '../../../infrastructure/acerill/custom-forms-v2-client';
import IntegrationOnlyForStandalone from '../common/integration-only-for-standalone';
import ConnectedAccounts from '../common/connected-accounts';

const GoogleSheets = () => {

  const navigate = useNavigate();

  const [accounts, setAccounts] = useState();
  const [integrations, setIntegrations] = useState();
  const [warningMessageCode, setWarningMessageCode] = useState();

  useEffect(() => {
    Promise
      .all([
        googleSheetsV1Client.findAccounts(),
        googleSheetsV1Client.findIntegrations(),
      ])
      .then(([accounts, integrations]) => {
        setAccounts(accounts);
        setIntegrations((integrations || [])
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        );
      })
      .catch(defaultErrorHandler);
    setWarningMessageCode(queryParameterUtils.tryFindQueryParameter('warning_message_code'));
  }, []);

  const connectAccount = accountName => {
    return redirectUtils.redirectTo(googleSheetsV1Client.getOauthStep1Url(accountName));
  };

  const deleteAccount = id => {
    return googleSheetsV1Client.deleteAccount(id)
      .then(() => setAccounts(prevAccounts => prevAccounts.filter(a => a.id !== id)));
  };

  const createIntegration = () => {
    return goToIntegration(uuidv4());
  };

  const goToIntegration = (id) => {
    navigate(`/integrations/google-sheets/${encodeURIComponent(id)}?shop=${encodeURIComponent(orgId)}`);
    return Promise.resolve(null);
  };

  const deleteIntegration = id => {
    return customFormsV2Client
      .deleteGoogleSheetsIntegration(id)
      .then(() => setIntegrations(prevIntegrations => prevIntegrations.filter(i => i.id !== id)));
  }

  const warningMessage = () => 'To setup the integration, the app needs access to your Google Sheets';

  return <Section title="Integration - Google Sheets" noMargin={true}>
    <div className="row justify-content-center">
      <div className="col-12 col-md-8">
        {warningMessageCode &&
        <WarningBox align="center">{warningMessage(warningMessageCode)}</WarningBox>
        }
        <IntegrationOnlyForStandalone/>
        <SubSection title="Connected accounts" className="mb-4">
          {!accounts ?
            <CenteredSpinner /> :
            <ConnectedAccounts accounts={accounts}
                               connectButtonClassName="p-0"
                               connectButtonContentFn={disabled => <img src={'/images/google-signin-dark-' + (disabled ? 'disabled' : 'normal') + '.png'} style={{marginTop: '-5px'}} alt="Google signin"/>}
                               connectAction={accountName => connectAccount(accountName)}
                               deleteAction={id => deleteAccount(id)}
                               errorHandler={defaultErrorHandler}/>
          }
        </SubSection>
        {(accounts || []).length > 0 &&
          <SubSection title="Integrations" className="mb-4">
            <NamedIntegrations integrations={integrations}
                               createAction={() => createIntegration()}
                               editAction={id => goToIntegration(id)}
                               deleteAction={id => deleteIntegration(id)}
                               errorHandler={defaultErrorHandler}/>
          </SubSection>
        }
      </div>
    </div>
  </Section>;
}

export default GoogleSheets;
